import { RefObject, useLayoutEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'

import { useOnClickOutside } from '../../../hooks/useOnClickOutside'

import { DropdownBackdrop, StyledDropdown } from './styles'

interface DropdownPosition {
  top: number
  right: number
}

interface DropdownProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper children type
  children: any
  display: boolean
  setDisplay: (v: boolean) => void
  /** When true, render a dimmed backdrop behind the menu. */
  showBackdrop?: boolean
  /** Anchor element used to position a portaled menu below the trigger. */
  anchorRef?: RefObject<HTMLElement>
  /** Render menu (and optional backdrop) in a portal with viewport-relative positioning. */
  portal?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper style type
  style?: any
}

function getAnchorPosition(anchor: HTMLElement): DropdownPosition {
  const rect = anchor.getBoundingClientRect()

  return {
    top: rect.bottom + 8,
    right: Math.max(16, window.innerWidth - rect.right),
  }
}

export const Dropdown = ({
  children,
  display,
  setDisplay,
  showBackdrop = false,
  anchorRef,
  portal = false,
  style,
}: DropdownProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper ref type
  const dropdownRef: any = useRef()
  const [portalPosition, setPortalPosition] = useState<DropdownPosition | null>(
    null,
  )

  useOnClickOutside(dropdownRef, () => setDisplay(false))

  useLayoutEffect(() => {
    if (!display || !portal || !anchorRef?.current) {
      return
    }

    const updatePosition = () => {
      if (anchorRef.current) {
        setPortalPosition(getAnchorPosition(anchorRef.current))
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchorRef, display, portal])

  if (!display) return <></>

  if (portal && !portalPosition) {
    return null
  }

  const dropdownStyle = portal
    ? {
        ...style,
        position: 'fixed',
        top: portalPosition?.top ?? 0,
        right: portalPosition?.right ?? 16,
        left: 'auto',
        bottom: 'auto',
        marginTop: 0,
        maxHeight: 'calc(100vh - 80px)',
      }
    : style

  const menu = (
    <StyledDropdown ref={dropdownRef} style={dropdownStyle} $portaled={portal}>
      {children}
    </StyledDropdown>
  )

  if (!portal) {
    return (
      <>
        {showBackdrop && <DropdownBackdrop onClick={() => setDisplay(false)} />}
        {menu}
      </>
    )
  }

  return createPortal(
    <>
      {showBackdrop && (
        <DropdownBackdrop onClick={() => setDisplay(false)} $portaled />
      )}
      {menu}
    </>,
    document.body,
  )
}
